import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";

import Request from "../common/Request.jsx";
import { CodeInput, CODE_LENGTH } from "./CodeInput";
import { safeReturnUrl } from "./handoff";
import { navigate } from "./navigate";
import { CheckIcon, CircleAlertIcon, EnvelopeIcon } from "./twoFactorIcons";

type Step = "loading" | "method" | "verify" | "done";

const COOLDOWN_MS = 120000;
const GENERIC_ERROR = "Something went wrong. Please try again.";
// Must match TwoFactorController.PostTwoFactor's 409 text: the API has no code to tell it from the mobile-client 409.
const ALREADY_ENABLED_MESSAGE = "Two-factor authentication is already on for this account.";
const HEADING_ID = "twofactor-heading";
const RAIL_STEPS: Array<{ id: "verify" | "done"; label: string }> = [
  { id: "verify", label: "Verify" },
  { id: "done", label: "Done" },
];

interface ApiResult {
  status: number;
  body: any;
}

// Session header only: Request.post/put add the onboarding API key, journey and UTM fields.
function sendTwoFactorRequest(method: "GET" | "POST", url: string, body?: unknown): Promise<ApiResult> {
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const session = Request.header.session();
  if (session) {
    headers[session.key] = session.value;
  }
  return Request.send(method, url, body, headers);
}

function defaultDestination(): string {
  const artcloud = window.Artcloud;
  if (!artcloud) {
    return "/";
  }
  const session = artcloud.storage.get(artcloud.storage.keys.session);
  const isCollector = session?.Type?.Value === 6;
  return (isCollector ? artcloud.url.mkt.baseUrl : artcloud.url.pro.baseUrl) ?? artcloud.url.mkt.baseUrl ?? "/";
}

function requestedReturnUrl(): string | null {
  return safeReturnUrl(new URLSearchParams(window.location.search).get("returnUrl"));
}

function resolveDestination(): string {
  return requestedReturnUrl() ?? defaultDestination();
}

// Carries only a requested returnUrl: without one, _postlogin picks the default from the fresh session.
export function loginUrl(): string {
  const login = window.Artcloud?.url.mkt.login() ?? "/login";
  const returnUrl = requestedReturnUrl();
  return returnUrl ? `${login}?returnUrl=${encodeURIComponent(returnUrl)}` : login;
}

// A wrap point before the @, so a long address in the half-width card breaks there rather than mid-word.
function breakableEmail(email: string) {
  const at = email.lastIndexOf("@");
  if (at <= 0) {
    return email;
  }
  return (
    <>
      {email.slice(0, at)}
      <wbr />
      {email.slice(at)}
    </>
  );
}

function resendLabel(remainingMs: number): string {
  if (remainingMs <= 0) {
    return "Resend code";
  }
  const totalSeconds = Math.ceil(remainingMs / 1000);
  if (totalSeconds >= 60) {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `Resend code in ${minutes}:${String(seconds).padStart(2, "0")}`;
  }
  return `Resend code in ${totalSeconds}s`;
}

function StepRail({ current }: { current: "verify" | "done" }) {
  const currentIndex = RAIL_STEPS.findIndex((step) => step.id === current);
  const fillPercent = Math.round(((currentIndex + 1) / RAIL_STEPS.length) * 100);
  return (
    <div className="twofactor__rail">
      {RAIL_STEPS.map((step, index) => {
        const isDone = index < currentIndex;
        const isCurrent = step.id === current;
        return (
          <div aria-current={isCurrent ? "step" : undefined} className="twofactor__rail-step" key={step.id}>
            <span
              className={
                isDone
                  ? "twofactor__rail-badge twofactor__rail-badge--done"
                  : isCurrent
                    ? "twofactor__rail-badge twofactor__rail-badge--current"
                    : "twofactor__rail-badge"
              }>
              {isDone ? "✓" : index + 1}
            </span>
            <span
              className={
                isCurrent ? "twofactor__rail-label twofactor__rail-label--current" : "twofactor__rail-label"
              }>
              {step.label}
            </span>
          </div>
        );
      })}
      <div className="twofactor__rail-track">
        <div className="twofactor__rail-fill" style={{ width: `${fillPercent}%` }} />
      </div>
    </div>
  );
}

export default function TwoFactorSetup() {
  const [step, setStep] = useState<Step>("loading");
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [verifyError, setVerifyError] = useState<string | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [cooldownEndsAt, setCooldownEndsAt] = useState<number | null>(null);
  const [, setTick] = useState(0);
  // A disabled control drops focus to <body>; bumping these hands it back once the request ends.
  const [cardFocusRequest, setCardFocusRequest] = useState(0);
  const [codeFocusRequest, setCodeFocusRequest] = useState(0);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const cardRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        const artcloud = window.Artcloud;
        const { status, body } = await sendTwoFactorRequest("GET", artcloud?.url.vendor.settings.twofactor.get() ?? "");
        if (cancelled) {
          return;
        }
        if (status === 401) {
          navigate(loginUrl());
          return;
        }
        if (status !== 200) {
          navigate(resolveDestination());
          return;
        }
        if (body?.Enabled) {
          navigate(resolveDestination());
          return;
        }
        setEmail(body?.Email ?? "");
        setStep("method");
      } catch {
        if (!cancelled) {
          navigate(resolveDestination());
        }
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Announce a step the user moved to; arriving on the page (loading → method) is not a move.
  const previousStepRef = useRef<Step>(step);
  useEffect(() => {
    const previous = previousStepRef.current;
    previousStepRef.current = step;
    if (previous !== "loading" && previous !== step && step !== "verify") {
      headingRef.current?.focus();
    }
  }, [step]);

  useEffect(() => {
    if (cooldownEndsAt === null) {
      return;
    }
    const interval = setInterval(() => {
      if (Date.now() >= cooldownEndsAt) {
        setCooldownEndsAt(null);
      } else {
        setTick((n) => n + 1);
      }
    }, 1000);
    return () => clearInterval(interval);
  }, [cooldownEndsAt]);

  useEffect(() => {
    if (cardFocusRequest > 0) {
      cardRef.current?.focus();
    }
  }, [cardFocusRequest]);

  const remainingMs = cooldownEndsAt === null ? 0 : Math.max(0, cooldownEndsAt - Date.now());
  // One request at a time: a resend landing mid-verify would retire the code being checked.
  const busy = sending || verifying;

  function leave() {
    navigate(resolveDestination());
  }

  function restoreFocus() {
    if (step === "method") {
      setCardFocusRequest((n) => n + 1);
    } else {
      setCodeFocusRequest((n) => n + 1);
    }
  }

  async function handleSend(onSuccess?: () => void) {
    if (sending || verifying) {
      return;
    }
    setSending(true);
    setSendError(null);
    setVerifyError(null);
    try {
      const artcloud = window.Artcloud;
      const { status, body } = await sendTwoFactorRequest("POST", artcloud?.url.vendor.settings.twofactor.post() ?? "");
      if (status === 204) {
        setCode("");
        setCooldownEndsAt(Date.now() + COOLDOWN_MS);
        setVerifyError(null);
        if (onSuccess) {
          onSuccess();
        } else {
          restoreFocus();
        }
        return;
      }
      if (status === 401) {
        navigate(loginUrl());
        return;
      }
      if (status === 409 && body?.Message === ALREADY_ENABLED_MESSAGE) {
        leave();
        return;
      }
      setSendError(body?.Message || GENERIC_ERROR);
      restoreFocus();
    } catch {
      setSendError(GENERIC_ERROR);
      restoreFocus();
    } finally {
      setSending(false);
    }
  }

  async function handleVerify(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (verifying || sending || code.length !== CODE_LENGTH) {
      return;
    }
    setVerifying(true);
    try {
      const artcloud = window.Artcloud;
      const { status, body } = await sendTwoFactorRequest(
        "POST",
        artcloud?.url.vendor.settings.twofactor.verify() ?? "",
        { Code: code },
      );
      if (status === 204) {
        setCooldownEndsAt(null);
        setStep("done");
        return;
      }
      if (status === 401) {
        navigate(loginUrl());
        return;
      }
      setVerifyError(body?.Message || GENERIC_ERROR);
      setCodeFocusRequest((n) => n + 1);
    } catch {
      setVerifyError(GENERIC_ERROR);
      setCodeFocusRequest((n) => n + 1);
    } finally {
      setVerifying(false);
    }
  }

  function handleCodeChange(value: string) {
    setCode(value);
    setVerifyError(null);
    setSendError(null);
  }

  if (step === "loading") {
    return null;
  }

  if (step === "method") {
    return (
      <div className="twofactor__step" data-step="method">
        <h1 className="twofactor__heading" id={HEADING_ID} ref={headingRef} tabIndex={-1}>
          Add an extra layer of security.
        </h1>
        <p className="twofactor__lead">Two-factor authentication asks for a short code when you log in.</p>
        <div className="twofactor__methods">
          <button
            className={
              sending ? "twofactor__method-card twofactor__method-card--chosen" : "twofactor__method-card"
            }
            data-testid="twofactor-email-card"
            disabled={sending}
            ref={cardRef}
            type="button"
            onClick={() => handleSend(() => setStep("verify"))}>
            <EnvelopeIcon />
            <span className="twofactor__method-text">
              <span className="twofactor__method-title">Email</span>
              <span className="twofactor__method-description">Send the code to {breakableEmail(email)}.</span>
            </span>
          </button>
        </div>
        <div className="twofactor__actions">
          <button className="twofactor__skip" data-testid="twofactor-skip" type="button" onClick={leave}>
            Skip for now
          </button>
          <span className="twofactor__note">You can turn this on later in Account settings.</span>
        </div>
        {sendError && (
          <div className="twofactor__error" role="alert">
            <CircleAlertIcon />
            <span>{sendError}</span>
          </div>
        )}
      </div>
    );
  }

  if (step === "verify") {
    const verifyStepError = verifyError ?? sendError;
    return (
      <>
        <StepRail current="verify" />
        <form className="twofactor__step" data-step="verify" noValidate onSubmit={handleVerify}>
          <h1 className="twofactor__heading" id={HEADING_ID} ref={headingRef} tabIndex={-1}>
            Enter the code we just sent.
          </h1>
          <p className="twofactor__lead">
            We emailed a six-digit code to {breakableEmail(email)}. It expires in ten minutes.
          </p>
          <CodeInput
            disabled={busy}
            error={Boolean(verifyError)}
            focusRequest={codeFocusRequest}
            labelledBy={HEADING_ID}
            value={code}
            onChange={handleCodeChange}
          />
          {verifyStepError && (
            <div className="twofactor__error" role="alert">
              <CircleAlertIcon />
              <span>{verifyStepError}</span>
            </div>
          )}
          <div className="twofactor__actions">
            <button
              className="twofactor__verify-button"
              data-testid="twofactor-verify"
              disabled={code.length !== CODE_LENGTH || busy}
              type="submit">
              Verify code
            </button>
            <button
              className="twofactor__resend"
              data-testid="twofactor-resend"
              disabled={remainingMs > 0 || busy}
              type="button"
              onClick={() => handleSend()}>
              {resendLabel(remainingMs)}
            </button>
          </div>
        </form>
      </>
    );
  }

  return (
    <>
      <StepRail current="done" />
      <div className="twofactor__step" data-step="done">
        <div className="twofactor__check-badge">
          <CheckIcon />
        </div>
        <h1 className="twofactor__heading" id={HEADING_ID} ref={headingRef} tabIndex={-1}>
          Two-factor authentication is on.
        </h1>
        <p className="twofactor__lead">We'll email a code to {breakableEmail(email)} when you log in.</p>
        <div className="twofactor__actions">
          <button
            className="twofactor__primary-button"
            data-testid="twofactor-done"
            type="button"
            onClick={leave}>
            Back to Artlogic Connect
          </button>
        </div>
      </div>
    </>
  );
}
